import { useVideaNotesCoreContext } from '../context/VideaNotesCoreContext';
import { useVideaNotesNoteResultContext } from '../context/VideaNotesNoteResultContext';
import { VideaNotesUIState } from '../types';

import { QuickNotePatientForm } from './QuickNotePatientForm';
import { NoteErrorView } from './slideOutViews/noteError/NoteErrorView';
import { NoteGenerationView } from './slideOutViews/noteGeneration/NoteGenerationView';
import {
  NoteResultBody,
  NoteResultHeader,
} from './slideOutViews/noteResult/NoteResultView';
import { RecordingFlowHeaderDropdown } from './slideOutViews/recordingFlow/RecordingFlowHeaderDropdown';
import { RecordingFlowHeaderLanguage } from './slideOutViews/recordingFlow/RecordingFlowHeaderLanguage';
import { RecordingFlowHeaderStatus } from './slideOutViews/recordingFlow/RecordingFlowHeaderStatus';
import { RecordingFlowView } from './slideOutViews/recordingFlow/RecordingFlowView';
import { VideaNotesContentHeader } from './VideaNotesContentHeader';

/**
 * Main content area for the VideaNotes sheet.
 * Consumes Core and NoteResult contexts for required state and handlers.
 */
export const VideaNotesMainContent = () => {
  const {
    uiStatus,
    patientName,
    processingError,
    onPatientCreated,
    onCloseSheet,
  } = useVideaNotesCoreContext();

  const { selectedNote } = useVideaNotesNoteResultContext();

  const headerRightContent =
    uiStatus === VideaNotesUIState.AUDIO_CAPTURE ? (
      <>
        <div className="vai-flex vai-items-center vai-gap-2 vai-min-w-0 vai-max-w-[300px] vai-flex-shrink-0">
          <div className="vai-shrink-0">
            <RecordingFlowHeaderLanguage />
          </div>
          <div className="vai-min-w-0 vai-flex-1">
            <RecordingFlowHeaderDropdown />
          </div>
        </div>
        <div className="vai-min-w-0 vai-max-w-[240px] vai-flex vai-items-center">
          <RecordingFlowHeaderStatus />
        </div>
      </>
    ) : uiStatus === VideaNotesUIState.SUMMARY_RESULT && selectedNote ? (
      <NoteResultHeader />
    ) : undefined;

  const headerRightSlotClassName =
    uiStatus === VideaNotesUIState.SUMMARY_RESULT
      ? 'vai-flex-1 vai-items-center vai-justify-end'
      : 'vai-flex-shrink-0 vai-gap-2';

  return (
    <div className="vai-relative vai-flex-1 vai-min-h-0 vai-flex vai-flex-col vai-gap-6">
      <VideaNotesContentHeader
        patientName={patientName}
        rightContent={headerRightContent}
        rightSlotClassName={headerRightSlotClassName}
      />

      {/* Patient info form (quick note) */}
      {uiStatus === VideaNotesUIState.PATIENT_INFO && (
        <QuickNotePatientForm
          onPatientCreated={onPatientCreated}
          onCancel={onCloseSheet}
        />
      )}

      {/* Template selection + recording in one flow; single AudioRecorder stays mounted */}
      {uiStatus === VideaNotesUIState.AUDIO_CAPTURE && <RecordingFlowView />}

      {/* Note generation loading */}
      {uiStatus === VideaNotesUIState.NOTE_GENERATION && <NoteGenerationView />}

      {/* Note result body (editor + action buttons) */}
      {uiStatus === VideaNotesUIState.SUMMARY_RESULT && selectedNote && (
        <NoteResultBody />
      )}

      {/* Error view */}
      {uiStatus === VideaNotesUIState.ERROR &&
        processingError !== undefined && (
          <NoteErrorView error={processingError} onDismiss={onCloseSheet} />
        )}
    </div>
  );
};
